<template>
<div class="px-1 pt-1">
  <div class="input-group pb-3">
    <button class="btn btn-outline-secondary" @click="refresh()" :disabled="isLoading">
      <div v-if="isLoading" class="spinner-border spinner-border-sm"></div>
      <font-awesome-icon v-else :icon="['fas', 'rotate']" />
      <span class="d-none d-sm-inline">{{ $t('action.refresh') }}</span>
    </button>
      <select id="supplier" name="labelSupplier" class="form-select" v-model="selectedSupplier">
        <option v-for="supplier in suppliers" :key="supplier" :value="supplier">{{ supplier }}</option>
      </select>
    <span class="input-group-text ms-2">
      <font-awesome-icon :icon="['fas', 'magnifying-glass']" />
    </span>
    <input id="itemFilter" type="search" class="form-control" v-model="itemFilter" :placeholder="$t('action.search')"/>
    <button v-if="itemFilter" type="button" class="btn btn-outline-secondary" @click="clearFilter" :title="$t('action.clear')">
      <font-awesome-icon :icon="['fas', 'filter-slash']" />
    </button>
  </div>

  <div v-if="error" class="alert alert-danger">
    {{ error }}
  </div>
  <div v-else-if="sortedDemands" id="content-page-table" class="table-fix-head" :style="fixHeadCalculatedStyle">
    <table v-if="sortedDemands.length > 0" class="table table-hover table-sm table-fit align-middle">
      <thead>
        <tr>
          <th>
            {{ $t('field.generic.item') }}
            <font-awesome-icon :icon="['fas', 'sort-up']" />
          </th>
          <th>{{ $t('page.stockDemand.table.quantityDemand') }}</th>
          <th class="d-none d-xl-table-cell">{{ $t('page.stockDemand.table.quantityDelivered') }}</th>
          <th class="d-none d-xl-table-cell">{{ $t('page.stockDemand.table.stockQuantity') }}</th>
        </tr>
      </thead>
      <tbody>
        <template v-for="(lines, siteName) in filteredItemsList" :key="siteName">
          <tr>
            <td class="d-none d-xl-table-cell text-primary" colspan="5"><h2>{{siteName}}</h2></td>
          </tr>
          <tr v-for="stockDemand in lines" :key="(stockDemand.orderId, stockDemand.item.id)">
          <td class="d-md-none">
            <span class="text-primary fw-bold">{{ stockDemand.item.name }}&#8203;</span><sup class="text-secondary ms-1">{{ stockDemand.item.code }}</sup><br>
            <span v-if="stockDemand.item.name.localeCompare(stockDemand.item.description) != 0" class="fs-6 text-melodi">{{ stockDemand.item.description }}</span>
          </td>
          <td class="d-none d-md-table-cell"><ItemField :item="stockDemand.item" :show-copy-button="true" /></td>
          <td>
            <UnitQuantity :quantity="stockDemand.quantityOrdered" :unit-name="stockDemand.unitCode" class="fw-bold fs-5" />
            <RelativeDateTime :at="stockDemand.orderDate" :use-since="true" style="font-size: 0.75em;" />
          </td>
          <td :class="demandClass(stockDemand)" v-if="stockDemand.quantityInDelivery" class="d-none d-xl-table-cell"><QuantityField :quantity="stockDemand.quantityInDelivery" :unit-name="stockDemand.unitCode" :pad-length="maxQuantityLength" /></td>
          <td v-else></td>
          <td class="d-none d-lg-table-cell"><QuantityField :quantity="stockDemand.stockQuantity" :unit-name="stockDemand.unitCode" :pad-length="maxQuantityLength" /></td>
        </tr>
        </template>
      </tbody>
    </table>
    <span v-else>{{ $t('error.noData') }}</span>
  </div>
  <div v-else class="loading">
    {{ $t('status.loadingEllipsis') }}
  </div>
</div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from "vue";
import { storeToRefs } from 'pinia';
import { ItemField, QuantityField } from "@/components/fields";
import UnitQuantity from "@/components/UnitQuantity.vue";
import RelativeDateTime from "@/components/RelativeDateTime.vue";
import { useOrderDemandStore } from "@/stores/orderDemand.store.ts";
import OrderDemand from "@/models/frontend/OrderDemand.ts";

const orderDemandStore = useOrderDemandStore()
const { data: orderLines, error } = storeToRefs(orderDemandStore);
const isLoading = ref(false);
const itemFilter = ref('');
const selectedSupplier = ref('');
const offsetTop = ref(0);

onMounted(async () => {
  await refresh();
  offsetTop.value = document.querySelector('#content-page-table')?.getBoundingClientRect().top ?? 0;
});

const fixHeadCalculatedStyle = computed(() => {
  return {
    height: `calc(100vh - ${offsetTop.value}px)`,
  };
});

const suppliers = computed(() => {
  return ['', ...new Set(orderLines.value?.map(line => line.supplier.name) || [])]
});

const filteredItemsList = computed(() => {
  let siteOrderLines = {} as Record<string, OrderDemand[]>;
  
  let lines = sortedDemands.value;
  
  if (selectedSupplier.value.trim()) {
    lines = lines.filter(entry => entry.supplier.name === selectedSupplier.value); 
  }
  
  if (itemFilter.value.trim()) {
    lines = lines.filter(entry => {
      const item = entry.item;
      return (
        item.name.toLowerCase().includes(itemFilter.value.toLowerCase().trim()) ||
        item.code.toLowerCase().includes(itemFilter.value.toLowerCase().trim()) ||
        (item.description && item.description.toLowerCase().includes(itemFilter.value.toLowerCase().trim()))
      );
    });
  } 
  
  if (lines) {
    lines.forEach(line => {
      if (!siteOrderLines[line.site.name]) {
        siteOrderLines[line.site.name] = [];
      }
      siteOrderLines[line.site.name]!.push(line);
    });
  }
 
  return siteOrderLines;
});

const clearFilter = () => {
  itemFilter.value = '';
};

async function refresh() {
  isLoading.value = true;
  error.value = "";

  try {
    await orderDemandStore.fetchAll();
  }
  finally {
    isLoading.value = false;
  }
}

const sortedDemands = computed(() => {
  return [...orderLines.value]?.sort((a, b) => a.item.name.localeCompare(b.item.name));
});

const maxQuantityLength = computed(() => {
  return sortedDemands.value.reduce((acc, item) => Math.max(acc, item.quantityOrdered.toString().length), 0);
});

function demandClass(orderDemand: OrderDemand) {
  return {
    'bg-success text-light': orderDemand.quantityInDelivery >= orderDemand.quantityOrdered,
    'bg-warning text-dark': orderDemand.quantityInDelivery < orderDemand.quantityOrdered,
  };
}
</script>

<style scoped type="scss">
.minmax-input {
  width: 220px;
}

input[type=number] {
  text-align: right;
}
</style>